/* global Highcharts:false */
/* eslint-disable */
import moment from 'moment';
import { get, sumBy } from 'lodash';
import constants from '@/constants';
import { NOTIFICATION_CONTRACT_OWNERSHIP } from '@/services/notifications/proxy';

export const capitalizeFirstLetter = string => string.charAt(0).toUpperCase() + string.slice(1);
export const getRedirectUrl = (path) => {
  const rootPath = process.env.ROOT_PATH.slice(0, -1);
  return `${window.location.protocol}//${window.location.host}${rootPath}${path}`;
};
export const DEFAULT_DATE_FORMAT = 'MM/DD/YYYY hh:mm a';
export const dateToString = (string, fmt = DEFAULT_DATE_FORMAT) => `${moment(string).format(fmt)} (ET)`;
export const priceDisplay = num => num.toFixed(2).toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
export const updateObjectValues = (obj, data, map = {}, setTimestampFor = [NOTIFICATION_CONTRACT_OWNERSHIP]) => {
  const result = { ...obj };
  Object.keys(data).forEach((key) => {
    const timestampKey = 'timestamp';
    const objKey = typeof map[key] !== 'undefined' ? map[key] : key;
    if (
      (typeof result[objKey] !== 'undefined' && objKey !== timestampKey) ||
      (setTimestampFor.includes(data.UpdateType) && objKey === timestampKey)) { // timestamp prop is updated only for certain notifications
      result[objKey] = data[key];
    }
  });

  return result;
};
export const capitalize = input => input.charAt(0).toUpperCase() + input.slice(1);
export const toCents = input => Math.round(100 * input);
export const correctPrice = input => (!isNaN(input) && input < 100 ? input : 0);
export const isNumber = input => !isNaN(parseFloat(input)) && isFinite(input);
export const isMarketSingle = market => market && (market.type === 'Single' || market.marketType === 0 || market.MarketType === 0);
export const contractOwnershipNum = contract => contract.userQuantity + contract.userOpenOrdersBuyQuantity + contract.userOpenOrdersSellQuantity;
export const isMarketSettled = market => market && market.status === constants.MARKET_SETTLED;
export const isMarketExpired = market => market && market.status === constants.MARKET_EXPIRED;
export const isMarketSuspended = market => market && market.status === constants.MARKET_SUSPENDED;
export const isMarketClosed = market => market && !market.isOpen;
export const hasMarketStatusMessage = market => isMarketClosed(market) && !!market.isOpenStatusMessage;
export const hasMarketPayout = market => market && (market.userInvestment !== 0 || market.userMaxPayout !== 0);
export const hasMarketHiddenContracts = (market) => {
  const contracts = (market.contracts || market.marketContracts) || [];
  return typeof contracts.find(i => i.hiddenByDefault) !== 'undefined';
};
export const isContractOpen = contract => (typeof contract.isOpen !== 'undefined' && contract.isOpen) ||
  (typeof contract.contractIsOpen !== 'undefined' && contract.contractIsOpen);
export const isHighChartsReady = () => typeof Highcharts !== 'undefined';
export default {};

export function largeNumberDisplay(x, largeThousands = true) {
  let value = x;
  if (value >= 1.0e+6) {
    if ((value / 1.0e+6).toFixed(1).endsWith('.0')) {
      value = `${(value / 1.0e+6).toFixed(0)}M`;
    } else {
      value = `${(value / 1.0e+6).toFixed(1)}M`;
    }
  } else if (largeThousands && value >= 1.0e+5) {
    value = `${(value / 1.0e+3).toFixed(0)}K`;
  } else if (!largeThousands && value >= 1.0e+3) {
    if ((value / 1.0e+3).toFixed(1).endsWith('.0')) {
      value = `${(value / 1.0e+3).toFixed(0)}K`;
    } else {
      value = `${(value / 1.0e+3).toFixed(1)}K`;
    }
  } else {
    value = value.toLocaleString();
  }
  return value;
}

export function elementOffset(el) {
  const rect = el.getBoundingClientRect();
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  return { top: rect.top + scrollTop, left: rect.left + scrollLeft };
}

export function marketUserGainLoss(market) {
  const contracts = (market.contracts || market.marketContracts) || [];
  return sumBy(
    contracts,
    (c) => {
      let gainLoss = 0;
      if (c.userPrediction === 0 && c.userOpenOrders !== 0) {
        gainLoss = (1 - c.lastTradePrice - c.userAveragePricePerShare) * c.userQuantity;
      } else {
        gainLoss = (c.lastTradePrice - c.userAveragePricePerShare) * c.userQuantity;
      }
      return gainLoss;
    },
  );
}

export const smartbtoa = input => btoa(unescape(encodeURIComponent(input)));
export const smartatob = input => decodeURIComponent(escape(atob(input)));

export const groupByKeepOrder = (arr, prop) => {
  const newArr = [];
  const wrapObj = {};

  arr.forEach((item) => {
    let propName = item[prop];
    if (propName) {
      propName = propName.toString();
      if (!wrapObj[propName]) {
        wrapObj[propName] = [];
        newArr.push(wrapObj[propName]);
      }
      wrapObj[propName].push(item);
    }
  });

  return newArr;
};

export function roundToTwo(value) {
  return (Math.round(value * 100) / 100);
}

export function roundDown(value) {
  return (Math.floor(value * 100) / 100);
}

export function roundTop(value) {
  return (Math.ceil(value * 100) / 100);
}

export const getCardTypeForNumber = (value) => {
  if (/^(?:4\d{12}(?:\d{3})?)$/.test(value)) return constants.CARDS.VISA;
  if (/^(?:3[47]\d{13})$/.test(value)) return constants.CARDS.AMERICAN_EXPRESS;
  if (/^6(?:011\d{12,15}|5\d{14,17}|4[4-9]\d{13,16}|22(?:1(?:2[6-9]|[3-9]\d)|[2-8]\d{2}|9(?:[01]\d|2[0-5]))\d{10,13})$/.test(value)) return constants.CARDS.DISCOVER;
  if (/^(?:5[1-5]\d{14})$/.test(value)) return constants.CARDS.MASTERCARD;
  return constants.CARDS.BLANK;
};

export const getOrdinalSuffix = (value) => {
  const symbols = ['th', 'st', 'nd', 'rd'];
  const v = value % 100;
  return (symbols[(v - 20) % 10] || symbols[v] || symbols[0]);
};

const loadedScripts = {}
export const loadScript = (url, windowVariable) => {
  if (!loadedScripts[url]) {
    loadedScripts[url] = new Promise((resolve, reject) => {
      let script = document.createElement('script')
      script.type = 'text/javascript'
      script.src = url
      script.addEventListener('load', () => resolve(windowVariable ? get(window, windowVariable) : undefined), false)
      script.addEventListener('error', e => reject(e), false)
      document.body.appendChild(script)
    })
  }
  return loadedScripts[url]
}

export const parseQuery = (queryString) => {
  const query = {};
  const a = ((queryString[0] === '?' || queryString[0] === '#') ? queryString.substr(1) : queryString).split('&');
  for (let i = 0; i < a.length; i += 1) {
    const b = a[i].split('=');
    query[decodeURIComponent(b[0])] = decodeURIComponent(b[1] || '');
  }
  return query;
}

export const getAssetPath = (path) => `${process.env.ASSETS_CDN_URL}${path}`;
export const stripTags = (string) =>
  (string || "").replace(/(<([^>]+)>)/gi, "");



// WEBPACK FOOTER //
// ./src/utils/helpers.js